<script context="module" lang="ts">
  export const ALL_BUs = 'all';
  export enum Theme {
    DARK_ON_LIGHT = 'darkOnLight',
    LIGHT_ON_DARK = 'lightOnDark'
  }
  export enum Align {
    CENTER = 'center',
    LEFT = 'left'
  }
</script>

<script lang="ts">
  import { cx } from '@emotion/css';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { BusinessUnit as BU } from '$lib/careers/types';
  import { accessibilityPreferences } from '$lib/stores/accessibility';
  import Left_Bracket_Light from './bu_lockup_assets/Left_Bracket_White.svg?component';
  import Left_Bracket_Dark from './bu_lockup_assets/Left_Bracket_Black.svg?component';
  import Right_Bracket_Light from './bu_lockup_assets/Right_Bracket_White.svg?component';
  import Right_Bracket_Dark from './bu_lockup_assets/Right_Bracket_Black.svg?component';

  export let businessUnit: BU | 'all' = ALL_BUs;
  export let renderBackground = false;
  export let monochrome = false;
  export let animated = true;
  export let theme: Theme = Theme.LIGHT_ON_DARK;
  export let alignment: Align = Align.CENTER;

  const responsiveVars = new ResponsiveVars()
    .addFluid('block-logo-height', toFluidVarInput(28, 40))
    .addFluid('block-logo-width', toFluidVarInput(24, 35))
    .addFluid('bu-size', toFluidVarInput(24, 34))
    .addFluid('bracket-height', toFluidVarInput(28, 40))
    .addFluid('bracket-width', toFluidVarInput(6, 8))
    .addFluid('block-bracket-gap', toFluidVarInput(9, 12))
    .addFluid('inner-single-gap', toFluidVarInput(6, 9))
    .addFluid('inner-multi-gap', toFluidVarInput(9, 12))
    .addFluid('padding-y', toFluidVarInput(9, 12))
    .addFluid('padding-x', toFluidVarInput(15, 24))
    .addFluid('extra-padding-right', toFluidVarInput(3, 3))
    .toCSS();

  let paths: Record<string, string> = {};
  let LeftBracket: typeof Left_Bracket_Light;
  let RightBracket: typeof Right_Bracket_Light;

  $: {
    const useLight = theme === Theme.LIGHT_ON_DARK;
    if (monochrome || $accessibilityPreferences.reduceColor) {
      paths[BU.AFTERPAY] = useLight ? 'Afterpay_Mono_White.svg' : 'Afterpay_Mono_Black.svg';
      paths[BU.BLOCK] = useLight ? 'Block_Mono_White.svg' : 'Block_Mono_Black.svg';
      paths[BU.SQUARE] = useLight ? 'Square_Mono_White.svg' : 'Square_Mono_Black.svg';
      paths[BU.CASHAPP] = useLight ? 'Cash_Mono_White.svg' : 'Cash_Mono_Black.svg';
      paths[BU.TIDAL] = useLight ? 'TIDAL_Mono_White.svg' : 'TIDAL_Mono_Black.svg';
      paths[BU.SPIRAL] = useLight ? 'Spiral_Mono_White.svg' : 'Spiral_Mono_Black.svg';
      paths[BU.PROTO] = useLight ? 'Proto_Mono_White.svg' : 'Proto_Mono_Black.svg';
      paths[BU.BITKEY] = useLight ? 'Bitkey_Mono_White.svg' : 'Bitkey_Mono_Black.svg';
    } else {
      paths[BU.AFTERPAY] = 'Afterpay_Color.svg';
      paths[BU.BLOCK] = 'Block_Color.png';
      paths[BU.SQUARE] = useLight ? 'Square_Mono_White.svg' : 'Square_Mono_Black.svg';
      paths[BU.CASHAPP] = 'Cash_Color.svg';
      paths[BU.TIDAL] = useLight ? 'TIDAL_Color_Light.svg' : 'TIDAL_Color_Dark.svg';
      paths[BU.SPIRAL] = useLight ? 'Spiral_Color_Light.svg' : 'Spiral_Color_Dark.svg';
      paths[BU.PROTO] = useLight ? 'Proto_Color_Light.svg' : 'Proto_Color_Dark.svg';
      paths[BU.BITKEY] = 'Bitkey_Color.svg';
    }
    LeftBracket = useLight ? Left_Bracket_Light : Left_Bracket_Dark;
    RightBracket = useLight ? Right_Bracket_Light : Right_Bracket_Dark;
  }
</script>

<div class={cx('wrapper', alignment)}>
  <div
    class={cx(
      'lockup',
      responsiveVars,
      theme,
      businessUnit === BU.BLOCK ? 'buNone' : businessUnit !== ALL_BUs ? 'buSingle' : 'buMulti',
      {
        static: !animated || $accessibilityPreferences.reduceMotion,
        renderBackground
      }
    )}
  >
    <div class="block">
      <img src={`/bu_logos/icon/${paths[BU.BLOCK]}`} alt="block logo" class="logo" />
    </div>
    {#if businessUnit !== BU.BLOCK}
      {#key $accessibilityPreferences.reduceColor}
        <div class="brackets">
          <svelte:component this={LeftBracket} class="bracket" />
          <div class="logoWrap">
            {#if businessUnit === ALL_BUs}
              <img src={`/bu_logos/icon/${paths[BU.SQUARE]}`} alt={BU.SQUARE} class="logo" />
              <img src={`/bu_logos/icon/${paths[BU.CASHAPP]}`} alt={BU.CASHAPP} class="logo" />
              <img src={`/bu_logos/icon/${paths[BU.AFTERPAY]}`} alt={BU.AFTERPAY} class="logo" />
              <img src={`/bu_logos/icon/${paths[BU.TIDAL]}`} alt={BU.TIDAL} class="logo" />
              <img src={`/bu_logos/icon/${paths[BU.BITKEY]}`} alt={BU.BITKEY} class="logo" />
              <img src={`/bu_logos/icon/${paths[BU.PROTO]}`} alt={BU.PROTO} class="logo" />
            {:else}
              <img src={`/bu_logos/icon/${paths[businessUnit]}`} alt={businessUnit} class="logo" />
            {/if}
          </div>
          <svelte:component this={RightBracket} class="bracket" />
        </div>
      {/key}
    {/if}
  </div>
</div>

<style lang="scss">
  .wrapper {
    display: flex;
    align-items: center;
    &.center {
      justify-content: center;
    }
    &.left {
      justify-content: left;
    }
  }

  .lockup {
    display: flex;
    align-items: center;
    color: black;
    align-items: center;

    &.lightOnDark {
      color: white;
    }

    &.darkOnLight {
      color: black;
    }

    &.renderBackground {
      border-radius: 500px;
      padding: var(--padding-y) var(--padding-x);
    }

    &.renderBackground.darkOnLight {
      background: white;
    }

    &.renderBackground.lightOnDark {
      background: black;
    }

    .block :global(> img),
    .block :global(> svg) {
      height: var(--block-logo-height);
      width: var(--block-logo-width);
    }

    .block,
    .brackets {
      display: flex;
      align-items: center;
      margin-left: 0;
    }

    @keyframes pop {
      0% {
        transform: scale(0.75);
      }
      86% {
        transform: scale(1.06);
      }
      100% {
        transform: scale(1);
      }
    }
    @keyframes slide {
      0% {
        opacity: 0;
        width: 0;
        height: 0;
        margin-left: 0px;
      }
      100% {
        opacity: 1;
        width: var(--width);
        height: var(--height);
        margin-left: var(--margin-left);
        margin-right: var(--margin-right);
      }
    }

    $step: 0.28s;

    .brackets :global(.bracket) {
      margin-left: 0;
      opacity: 0;
      transform: scale(0);
      height: 0;
      width: 0;
      --width: var(--bracket-width);
      --height: var(--bracket-height);
      --margin-left: var(--block-bracket-gap);
      --margin-right: 0;
      animation:
        pop $step ease-in-out forwards,
        slide 0.18s cubic-bezier(0.28, 0.07, 0.31, 1.01) forwards;
      animation-delay: 0.47s + $step * 2;

      &:last-child {
        --margin-right: var(--extra-padding-right);
      }
    }

    .logoWrap {
      transition: width 0.5s ease;
      display: flex;
      align-items: center;
    }

    &:global(.buSingle .logo),
    &:global(.buSingle .bracket:last-child) {
      --margin-left: var(--inner-single-gap);
    }
    &:global(.buMulti .logo),
    &:global(.buMulti .bracket:last-child) {
      --margin-left: var(--inner-multi-gap);
    }

    .brackets :global(.logo) {
      opacity: 0;
      width: 0;
      height: 0;
      transform: scale(0);
      margin-left: 0;
      --width: var(--bu-size);
      --height: var(--bu-size);
      animation:
        pop $step ease-in-out forwards,
        slide 0.19s cubic-bezier(0.28, 0.07, 0.31, 1.01) forwards;

      @for $i from 1 through 10 {
        &:nth-child(#{$i}) {
          animation-delay: 0.47s + $step * 2 + $i * $step;
        }
      }
    }
  }
  .static {
    .brackets :global(.logo) {
      opacity: 1;
      width: var(--bu-size);
      height: var(--bu-size);
      transform: scale(1);
      margin-left: var(--margin-left);
      animation: none !important;
    }
    .brackets :global(.bracket) {
      animation: none !important;
      opacity: 1;
      transform: scale(1);
      height: var(--bracket-height);
      width: var(--bracket-width);
      margin-left: var(--margin-left);
    }
  }
</style>
